import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Music } from "lucide-react";
import { fileUrl } from "@/lib/api";

/**
 * Lightweight, premium audio player for project pages.
 * - Lazy-loads the audio (preload="metadata") so it doesn't block page perf.
 * - Honors admin-controlled autoplay (only after a user gesture for browsers
 *   that block autoplay; we attempt and silently fall back to muted-on-load).
 */
export default function AudioPlayer({ url, title, autoplay = false }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => { setDuration(a.duration || 0); setReady(true); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
  }, []);

  // Autoplay attempt (browsers may block; we silently fall back)
  useEffect(() => {
    if (!autoplay || !ready) return;
    const a = audioRef.current;
    if (!a) return;
    const tryPlay = async () => {
      try { await a.play(); }
      catch {
        // Most browsers block autoplay with audio. Try muted as a softer fallback.
        try { a.muted = true; setMuted(true); await a.play(); } catch { /* no-op */ }
      }
    };
    tryPlay();
  }, [autoplay, ready]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (a.paused) await a.play();
      else a.pause();
    } catch { /* ignore */ }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = Math.max(0, Math.min(duration, ratio * duration));
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  if (!url) return null;

  return (
    <div className="px-4 md:px-5 py-3 border-t border-[color:var(--bda-border)]" data-testid="project-audio-player">
      <audio ref={audioRef} src={fileUrl(url)} preload="metadata" muted={muted} />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          data-testid="audio-toggle"
          className="flex-shrink-0 w-10 h-10 rounded-full bg-[color:var(--bda-blue-glow)] hover:bg-[color:var(--bda-blue)] text-white flex items-center justify-center transition-colors"
        >
          {playing ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 text-[11px] tracking-wider uppercase text-[color:var(--bda-text-muted)]">
            <Music className="w-3 h-3" strokeWidth={1.6} />
            <span className="truncate" data-testid="audio-title">{title || "Soundtrack"}</span>
            <span className="ml-auto font-mono text-[10px] text-[color:var(--bda-text-dim)]">
              {fmt(progress)} / {fmt(duration)}
            </span>
          </div>
          <div
            onClick={seek}
            className="h-1 bg-[color:var(--bda-surface-2)] rounded-full overflow-hidden cursor-pointer"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={progress}
          >
            <div
              className="h-full bg-[color:var(--bda-blue-glow)] transition-[width] duration-100"
              style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => { const a = audioRef.current; if (a) { a.muted = !a.muted; setMuted(a.muted); } }}
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid="audio-mute"
          className="flex-shrink-0 p-2 text-[color:var(--bda-text-muted)] hover:text-[color:var(--bda-text)]"
        >
          {muted ? <VolumeX className="w-4 h-4" strokeWidth={1.6} /> : <Volume2 className="w-4 h-4" strokeWidth={1.6} />}
        </button>
      </div>
    </div>
  );
}
